import React, { useState, useEffect } from 'react';
import Icon from '../../common/Icon';

const FindAndReplace = ({
  fieldsets,
  determineField,
  selectedField,
  submissionCount,
  textState,
  setTextState,
  handleClearText,
  handleUpdateText,
}) => {
  const [prepend, setPrepend] = useState(false);
  const [append, setAppend] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showFindExample, setShowFindExample] = useState(false);
  const dropdown = showOptions ? 'dropdown-show' : 'dropdown-hide';
  const { old_txt, new_txt, remove_whitespace, prepend_txt, append_txt } =
    textState;
  const optionsAreAdded = remove_whitespace || prepend_txt || append_txt;

  const toggleShowOptions = () => {
    if (!optionsAreAdded) setShowOptions(!showOptions);
  };

  useEffect(() => {
    if (prepend) $('#prepend_txt')[0].focus();
    if (append) $('#append_txt')[0].focus();
  }, [prepend, append]);

  useEffect(() => {
    setPrepend(false);
    setAppend(false);
  }, []);

  const handleTextState = (key, value) => {
    setTextState({ ...textState, [key]: value });
  };
  const canUpdate =
    selectedField &&
    (old_txt || new_txt || remove_whitespace || prepend_txt || append_txt);

  const shorten = (string) =>
    string.length < 12 ? string : string.slice(0, 12) + '...';

  const determineResult = () => {
    let textEdit = '';
    if (!selectedField) {
      return 'No change...';
    } else if (old_txt && new_txt) {
      textEdit = `Replace instances of "${shorten(old_txt)}" with "${shorten(
        new_txt
      )}".`;
    } else if (old_txt) {
      textEdit = `Delete instances of "${old_txt}".`;
    } else if (new_txt) {
      textEdit = `Assign empty response values with "${new_txt}".`;
    } else if (
      selectedField &&
      !remove_whitespace &&
      !prepend_txt &&
      !append_txt
    ) {
      return 'No change...';
    }

    if (prepend_txt) {
      textEdit = `Prepend all values with "${prepend_txt}". ` + textEdit;
    }

    if (append_txt) {
      textEdit = textEdit + ` Append all values with "${append_txt}".`;
    }

    if (remove_whitespace) {
      textEdit = textEdit + ' Remove leading, trailing, and extra spaces.';
    }
    return textEdit;
  };

  const manipulateExample = (original) => {
    let example = original;
    let oldMatch = new RegExp(old_txt, 'g');
    if (old_txt) example = example.replace(oldMatch, new_txt);
    example = prepend_txt + example + append_txt;
    if (remove_whitespace) example = example.trim().replace(/\s+/g, ' ');
    return example;
  };

  const originalExample = `${old_txt || '[FIND]'} [other text]`;
  const resultExample = manipulateExample(originalExample);
  const result = determineResult();

  return (
    <>
      <div className='creation-form'>
        <div className='row'>
          <h3>Field to update:</h3>
          <div className='full'>
            <div className='select-input'>
              <select
                name='field-select'
                id='field-select'
                onChange={(e) => determineField(e.target.value)}
              >
                <option label='Please select...' value={''} />
                {fieldsets.map((fs) => {
                  return (
                    <optgroup key={fs.id} label={fs.name}>
                      {fs.fields
                        .filter((f) => f.type === 'text')
                        .map((field) => {
                          return (
                            <option
                              key={field.id}
                              label={field.name}
                              value={field.id}
                            />
                          );
                        })}
                    </optgroup>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
        <div className='controls'>
          <button
            onClick={handleClearText}
            className='danger'
            disabled={!selectedField}
          >
            Clear Response Values
          </button>
        </div>

        <div className='row'>
          <h3>Find text:</h3>
          <div className='full'>
            {selectedField ? (
              <input
                placeholder='Ex: Independent School District'
                type='text'
                onChange={(e) => handleTextState('old_txt', e.target.value)}
              />
            ) : (
              <input
                placeholder='Ex: Independent School District'
                type='text'
                disabled
              />
            )}
            <em>Case-sensitive, only matches whole word instances.</em>
            {showFindExample ? (
              <>
                <p>
                  Example: <b>"pen"</b>{' '}
                  <Icon
                    icon='minus-circle'
                    onClick={() => setShowFindExample(false)}
                  />
                </p>
                <p>
                  <em>
                    "Written in <b>pen</b>"
                  </em>{' '}
                  <i className='fa-regular fa-circle-check fa-lg green' />{' '}
                  Matches
                </p>
                <p>
                  <em>
                    "Inde
                    <b>pen</b>dent <b>Pen</b>"
                  </em>{' '}
                  <i className='fa-solid fa-ban fa-lg red' /> Does not match
                </p>
              </>
            ) : (
              <p>
                Show Example{' '}
                <Icon
                  icon='plus-circle'
                  onClick={() => setShowFindExample(true)}
                />
              </p>
            )}
          </div>
        </div>

        <div className='row'>
          <h3>Replace with:</h3>
          <div className='full'>
            {selectedField ? (
              <input
                placeholder='Ex: ISD'
                type='text'
                onChange={(e) => handleTextState('new_txt', e.target.value)}
              />
            ) : (
              <input placeholder='Ex: ISD' type='text' disabled />
            )}
          </div>
        </div>

        <h4>
          Additional Options{' '}
          <Icon
            icon={showOptions ? 'chevron-circle-up' : 'chevron-circle-down'}
            onClick={toggleShowOptions}
          />
        </h4>

        <div className={`additional-options ${dropdown}`}>
          <div className='row'>
            <em>
              Tip: You may want to include a space in the appropriate position
              when prepending or appending!
            </em>
          </div>
          <div className='row'>
            <div className='half'>
              <input
                id='prepend'
                type='checkbox'
                onChange={(e) => {
                  if (!e.target.checked) handleTextState('prepend_txt', '');
                  setPrepend(e.target.checked);
                }}
                checked={prepend}
                disabled={!selectedField}
              />
              <label htmlFor='prepend'>Prepend Value</label>
            </div>
            <div className='half'>
              <input
                type='text'
                id='prepend_txt'
                placeholder={'[PREPEND]Response'}
                disabled={!prepend}
                value={prepend_txt}
                onChange={(e) => handleTextState('prepend_txt', e.target.value)}
              />
            </div>
          </div>

          <div className='row'>
            <div className='half'>
              <input
                id='append'
                type='checkbox'
                onChange={(e) => {
                  if (!e.target.checked) handleTextState('append_txt', '');
                  setAppend(e.target.checked);
                }}
                checked={append}
                disabled={!selectedField}
              />
              <label htmlFor='append'>Append Value</label>
            </div>
            <div className='half'>
              <input
                type='text'
                id='append_txt'
                placeholder={'Response[APPEND]'}
                disabled={!append}
                value={append_txt}
                onChange={(e) => handleTextState('append_txt', e.target.value)}
              />
            </div>
          </div>

          <div className='row'>
            <div className='half'>
              <input
                id='remove_whitespace'
                type='checkbox'
                onChange={(e) => {
                  let value = e.target.checked ? 'true' : '';
                  handleTextState('remove_whitespace', value);
                }}
                checked={remove_whitespace === 'true' ? true : false}
                disabled={!selectedField}
              />
              <label htmlFor='remove_whitespace'>Trim Extra Spaces</label>
            </div>
          </div>
        </div>

        <h3>Result:</h3>
        <div className='row'>
          <b>{result}</b>
          <p>Original: {originalExample}</p>
          <p>Result: {resultExample}</p>
        </div>

        <div className='controls lower'>
          <div className='right'>
            <h3>Submission count: {submissionCount}</h3>

            <button disabled={!canUpdate} onClick={handleUpdateText}>
              Update Submissions
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FindAndReplace;
